import { useState } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    Filter,
    Loader2,
    Send,
} from "lucide-react";
import { adminApi } from "@/components/Admin/api";
import { formatRel } from "@/components/Admin/format";

export default function WebhookPanel({ webhookLog, onAfterTest }) {
    const [whUrl, setWhUrl] = useState("");
    const [whSecret, setWhSecret] = useState("");
    const [result, setResult] = useState(null);
    const [sending, setSending] = useState(false);

    const sendTest = async () => {
        setSending(true);
        setResult(null);
        try {
            const body = {};
            if (whUrl.trim()) body.target_url = whUrl.trim();
            if (whSecret.trim()) body.secret = whSecret.trim();
            const r = await adminApi.post("/webhook/test", body);
            setResult(r.data);
            if (onAfterTest) await onAfterTest();
        } catch (err) {
            setResult({
                delivered: false,
                error: err?.message || "Test request failed",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="mt-12 grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <h3 className="font-display text-2xl leading-none">
                    Webhook tester
                </h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                    Posts a synthetic{" "}
                    <code className="text-zinc-300">webhook_test</code> event.
                    Leave fields blank to use the values from the backend
                    environment.
                </p>

                <div className="mt-5 space-y-3">
                    <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] uppercase tracking-widest text-zinc-400">
                            Override URL (optional)
                        </span>
                        <input
                            type="url"
                            value={whUrl}
                            onChange={(e) => setWhUrl(e.target.value)}
                            placeholder="https://yourcrm.example.com/edara"
                            className="px-3.5 py-3 rounded-lg border border-white/10 bg-[#0A0A0A] text-white placeholder-zinc-600 focus:border-[#1FA8FF] focus:ring-2 focus:ring-[#1FA8FF]/30 outline-none"
                            data-testid="webhook-url"
                        />
                    </label>
                    <label className="flex flex-col gap-1.5">
                        <span className="text-[11px] uppercase tracking-widest text-zinc-400">
                            Override secret (optional)
                        </span>
                        <input
                            type="text"
                            value={whSecret}
                            onChange={(e) => setWhSecret(e.target.value)}
                            placeholder="hmac-sha256 secret"
                            className="px-3.5 py-3 rounded-lg border border-white/10 bg-[#0A0A0A] text-white placeholder-zinc-600 focus:border-[#1FA8FF] focus:ring-2 focus:ring-[#1FA8FF]/30 outline-none"
                            data-testid="webhook-secret"
                        />
                    </label>
                    <button
                        onClick={sendTest}
                        disabled={sending}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#1FA8FF] text-white font-semibold hover:bg-[#0C84D4] disabled:opacity-60"
                        data-testid="webhook-test"
                    >
                        {sending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Sending…
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                Fire test event
                            </>
                        )}
                    </button>

                    {result && <Banner result={result} />}
                </div>
            </div>

            <RecentDeliveries webhookLog={webhookLog} />
        </section>
    );
}

function RecentDeliveries({ webhookLog }) {
    return (
        <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl leading-none">
                    Recent deliveries
                </h3>
                <Filter className="w-4 h-4 text-zinc-500" />
            </div>
            <p className="mt-2 text-sm text-zinc-400">
                Last 25 outbound webhook attempts (newest first).
            </p>
            <div className="mt-5 divide-y divide-white/5">
                {webhookLog.length === 0 && (
                    <p className="py-10 text-center text-zinc-500 text-sm">
                        No deliveries yet.
                    </p>
                )}
                {webhookLog.map((row) => (
                    <div key={row.id} className="py-3 flex items-center gap-4">
                        <span
                            className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                                row.delivered
                                    ? "bg-[#10B981]"
                                    : "bg-[#F59E0B]"
                            }`}
                        />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 text-sm">
                                <span className="font-semibold text-white">
                                    {row.event}
                                </span>
                                <span className="text-zinc-500">
                                    {row.status_code
                                        ? `HTTP ${row.status_code}`
                                        : row.error
                                          ? "error"
                                          : "no response"}
                                </span>
                            </div>
                            <div className="text-xs text-zinc-500 truncate">
                                {row.url}
                                {row.error ? ` · ${row.error}` : ""}
                            </div>
                        </div>
                        <div className="text-xs text-zinc-500 shrink-0">
                            {formatRel(row.at)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Banner({ result }) {
    return (
        <div
            className={`rounded-lg p-3 border text-sm ${
                result.delivered
                    ? "border-[#10B981]/30 bg-[#10B981]/10"
                    : "border-[#F59E0B]/30 bg-[#F59E0B]/10"
            }`}
            data-testid="webhook-result"
        >
            <div className="flex items-center gap-2 font-semibold">
                {result.delivered ? (
                    <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                ) : (
                    <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />
                )}
                {result.delivered ? "Delivered" : "Not delivered"}
                {result.status_code ? ` · HTTP ${result.status_code}` : ""}
            </div>
            {result.reason && (
                <p className="mt-1.5 text-zinc-400">{result.reason}</p>
            )}
            {result.error && (
                <p className="mt-1.5 text-zinc-400">{result.error}</p>
            )}
            {result.url && (
                <p className="mt-1.5 text-zinc-500 truncate">→ {result.url}</p>
            )}
        </div>
    );
}
